<template>
  <v-dialog
    :value="value"
    max-width="1100px"
    persistent
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="d-flex flex-wrap align-center">
        <span class="headline mr-2">ຈັດການ Role ແລະ Permission ຂອງຜູ້ໃຊ້</span>
        <v-chip
          label
          color="primary"
          class="mr-2"
        >
          {{ userLabel }}
        </v-chip>
        <span class="body-2 grey--text">
          ສິດທີ່ໃຊ້ໄດ້ທັງໝົດ {{ effectiveCount }} / {{ allNames.length }}
        </span>
      </v-card-title>

      <v-alert
        v-if="customerUser"
        type="warning"
        dense
        text
        class="mx-4 mb-0"
      >
        ຜູ້ໃຊ້ນີ້ເປັນລູກຄ້າ (pre_customer, customer, company): ປ່ຽນ Role ແລະ Permission ບໍ່ໄດ້.
      </v-alert>

      <v-divider />
      <v-card-text style="height: 68vh">
        <v-row
          v-if="loading"
          justify="center"
          class="my-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-row>
        <template v-else>
          <!-- Roles -->
          <div class="subtitle-1 font-weight-bold mt-3 mb-1">
            <v-icon
              small
              class="mr-1"
            >
              mdi-account-key
            </v-icon>
            1. Role
            <span class="caption grey--text font-weight-regular">
              (ຜູ້ໃຊ້ໄດ້ທຸກສິດຂອງ Role ທີ່ຕິກ)
            </span>
          </div>
          <div
            v-if="lockedRoles.length"
            class="caption mb-1"
          >
            Role ທີ່ປ່ຽນຢູ່ນີ້ບໍ່ໄດ້:
            <v-chip
              v-for="name in lockedRoles"
              :key="name"
              x-small
              label
              class="mr-1"
            >
              {{ name }}
            </v-chip>
          </div>
          <v-row dense>
            <v-col
              v-for="role in roles"
              :key="role.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-checkbox
                v-model="selectedRoles"
                :value="role.name"
                :disabled="customerUser || isCustomerRole(role.name)"
                hide-details
                dense
                class="mt-0 pt-1"
              >
                <template v-slot:label>
                  <div>
                    <div class="body-2 black--text">
                      {{ role.name }}
                      <v-chip
                        v-if="rolesAdded.includes(role.name)"
                        x-small
                        color="success"
                        class="ml-1"
                      >
                        ເພີ່ມ
                      </v-chip>
                      <v-chip
                        v-if="rolesRemoved.includes(role.name)"
                        x-small
                        color="error"
                        class="ml-1"
                      >
                        ຖອນ
                      </v-chip>
                    </div>
                    <div class="caption grey--text">
                      {{ isCustomerRole(role.name) ? 'ສຳລັບລູກຄ້າ' : `${(role.permissions || []).length} permission` }}
                    </div>
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Direct permissions -->
          <div class="d-flex flex-wrap align-center mb-2">
            <div class="subtitle-1 font-weight-bold mr-2">
              <v-icon
                small
                class="mr-1"
              >
                mdi-shield-key
              </v-icon>
              2. Permission ເພີ່ມເຕີມ
              <span class="caption grey--text font-weight-regular">
                (ໃຫ້ສະເພາະຜູ້ໃຊ້ຄົນນີ້, ນອກຈາກສິດຂອງ Role)
              </span>
            </div>
            <v-spacer />
            <v-text-field
              v-model="search"
              clearable
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              label="ຄົ້ນຫາ permission"
              style="max-width: 280px"
            />
          </div>
          <PermissionGroupPicker
            v-model="selectedPermissions"
            :names="allNames"
            :original="originalPermissions"
            :inherited="inherited"
            :search="search || ''"
            :disabled="customerUser"
          />
        </template>
      </v-card-text>
      <v-divider />

      <v-card-actions class="flex-wrap">
        <span class="body-2 mr-4">
          Role:
          <span class="success--text mr-2">+ {{ rolesAdded.length }}</span>
          <span class="error--text">− {{ rolesRemoved.length }}</span>
        </span>
        <span class="body-2">
          Permission:
          <span class="success--text mr-2">+ {{ permissionsAdded.length }}</span>
          <span class="error--text">− {{ permissionsRemoved.length }}</span>
        </span>
        <v-spacer />
        <v-btn
          text
          :disabled="saving"
          @click="close"
        >
          ຍົກເລີກ
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="saving || customerUser || !changed"
          @click="save"
        >
          ບັນທຶກ
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PermissionGroupPicker from '../roles/PermissionGroupPicker.vue';
import { CUSTOMER_ROLES } from '../roles/permissionCatalog';

const diff = (from, to) => to.filter((name) => !from.includes(name));

export default {
  name: 'UserAccessEditor',
  components: { PermissionGroupPicker },
  props: {
    value: { type: Boolean, default: false },
    user: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      roles: [],
      allNames: [],
      originalRoles: [],
      selectedRoles: [],
      originalPermissions: [],
      selectedPermissions: [],
      search: '',
      loading: false,
      saving: false,
    };
  },
  computed: {
    userLabel() {
      const name = [this.user.name, this.user.surname].filter(Boolean).join(' ');
      return name || this.user.email || this.user.phone || `#${this.user.id}`;
    },
    userRoleNames() {
      return (this.user.roles || []).map((role) => role.name);
    },
    customerUser() {
      return this.userRoleNames.some((name) => CUSTOMER_ROLES.includes(name));
    },
    // Roles the user holds that this list cannot change (super_admin is not listed).
    lockedRoles() {
      const listed = this.roles.map((role) => role.name);
      return this.userRoleNames.filter((name) => !listed.includes(name));
    },
    // Permission name -> the ticked roles that already give it.
    inherited() {
      const map = {};
      this.roles
        .filter((role) => this.selectedRoles.includes(role.name))
        .forEach((role) => (role.permissions || []).forEach((permission) => {
          (map[permission.name] = map[permission.name] || []).push(role.name);
        }));
      return map;
    },
    effectiveCount() {
      return new Set([...Object.keys(this.inherited), ...this.selectedPermissions]).size;
    },
    rolesAdded() {
      return diff(this.originalRoles, this.selectedRoles);
    },
    rolesRemoved() {
      return diff(this.selectedRoles, this.originalRoles);
    },
    permissionsAdded() {
      return diff(this.originalPermissions, this.selectedPermissions);
    },
    permissionsRemoved() {
      return diff(this.selectedPermissions, this.originalPermissions);
    },
    changed() {
      return this.rolesAdded.length || this.rolesRemoved.length
        || this.permissionsAdded.length || this.permissionsRemoved.length;
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
  },
  methods: {
    isCustomerRole(name) {
      return CUSTOMER_ROLES.includes(name);
    },
    load() {
      this.search = '';
      this.originalPermissions = (this.user.permissions || []).map((permission) => permission.name);
      this.selectedPermissions = [...this.originalPermissions];
      this.loading = true;
      Promise.all([
        this.$axios.get('user-setting/role'),
        this.$axios.get('user-setting/permission'),
      ])
        .then(([roles, permissions]) => {
          const list = roles.data.data;
          this.roles = Array.isArray(list) ? list : (list.data || []);
          this.allNames = (permissions.data.data || []).map((permission) => permission.name);
          const listed = this.roles.map((role) => role.name);
          this.originalRoles = this.userRoleNames.filter((name) => listed.includes(name));
          this.selectedRoles = [...this.originalRoles];
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    async save() {
      this.saving = true;
      const base = `user-setting/user/${this.user.id}`;
      try {
        if (this.rolesAdded.length) await this.$axios.post(`${base}/give-role`, { roles: this.rolesAdded });
        if (this.rolesRemoved.length) await this.$axios.post(`${base}/revoke-role`, { roles: this.rolesRemoved });
        if (this.permissionsAdded.length) {
          await this.$axios.post(`${base}/give-permission`, { permissions: this.permissionsAdded });
        }
        if (this.permissionsRemoved.length) {
          await this.$axios.post(`${base}/revoke-permission`, { permissions: this.permissionsRemoved });
        }
        this.toast('success', 'ບັນທຶກ Role ແລະ Permission ແລ້ວ');
      } catch (error) {
        // Earlier steps may have succeeded: the parent reloads the server's state, and the
        // editor closes so it is reopened on that state.
        this.toast('error', this.message(error));
      } finally {
        this.saving = false;
        this.$emit('saved');
        this.$emit('input', false);
      }
    },
    close() {
      this.$emit('input', false);
    },
    message(error) {
      return (error.response && error.response.data && error.response.data.message) || 'Something went wrong';
    },
    toast(color, msg) {
      this.$store.commit('Toast_State', { value: true, color, msg });
    },
  },
};
</script>
